import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Calendar,
  CreditCard,
  FileText,
  User,
  LogOut,
  ExternalLink,
  ShieldCheck,
  CheckCircle,
  AlertTriangle,
  Clock
} from "lucide-react";
import { API_BASE_URL } from "../config";
import vrmLogo from "../assets/VRM_New.jpeg";
import "../styles/buyer.css";

function BuyerDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    company: null,
    subscriptions: [],
    purchases: [],
    payments: [],
    totalActiveProducts: 0,
  });
  const [agreement, setAgreement] = useState(null);
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);

  const token = localStorage.getItem("buyerToken");
  const buyerName = localStorage.getItem("buyerName") || "Buyer";
  const companyName = localStorage.getItem("buyerCompanyName") || "";

  useEffect(() => {
    if (!token) {
      navigate("/buyer/login");
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API_BASE_URL}/api/buyer/dashboard`, { headers }),
      axios.get(`${API_BASE_URL}/api/buyer/agreements`, { headers }).catch(() => ({ data: null })),
    ])
      .then(([dashRes, agRes]) => {
        setData(dashRes.data);
        if (agRes && agRes.data) {
          setAgreement(agRes.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load buyer data:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("buyerToken");
          navigate("/buyer/login");
        }
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("buyerToken");
    localStorage.removeItem("buyerCompanyId");
    localStorage.removeItem("buyerCompanyName");
    localStorage.removeItem("buyerName");
    navigate("/buyer/login");
  };

  const handleDownloadAgreement = async () => {
    try {
      setDownloadingAgreement(true);

      const category = company?.productCategory || localStorage.getItem("productCategory");
      const adminProducts = JSON.parse(localStorage.getItem("vrm_admin_products") || "[]");
      const selectedAdminProduct = adminProducts.find(
        (p) => p.slug === category || 
               p.name.toLowerCase() === (category || "").toLowerCase() ||
               (p.category && p.category.toLowerCase() === (category || "").toLowerCase())
      );

      if (selectedAdminProduct && selectedAdminProduct.agreementPdf && selectedAdminProduct.agreementPdf.dataUrl) {
        const win = window.open();
        if (win) {
          win.document.write(`<html><head><title>${selectedAdminProduct.name} Agreement</title></head><body style="margin:0;"><iframe src="${selectedAdminProduct.agreementPdf.dataUrl}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100vh;" allowfullscreen></iframe></body></html>`);
        } else {
          window.open(selectedAdminProduct.agreementPdf.dataUrl, "_blank");
        }
        setDownloadingAgreement(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const verifiedPayment = (payments || []).find(payment => payment.paymentVerified && payment.transactionId);
      if (!verifiedPayment) throw new Error("No verified payment transaction found for this account.");

      const res = await axios.get(
        `${API_BASE_URL}/api/onboarding/agreement/order/${encodeURIComponent(verifiedPayment.transactionId)}`,
        { headers, responseType: "blob" }
      ).catch(() => {
        // Fallback: preview endpoint
        const comp = data.company || {};
        return axios.post(`${API_BASE_URL}/onboarding/agreement/preview`, {
          companyName: comp.companyName,
          address: comp.address,
          companyEmail: comp.companyEmail,
          phone: comp.phone,
          agreementSignature: comp.agreementSignature,
          productCategory: comp.productCategory,
          purchaserName: comp.purchaserName,
          purchaserDesignation: comp.purchaserDesignation
        }, { responseType: "blob" });
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert("Could not load agreement PDF.");
    } finally {
      setDownloadingAgreement(false);
    }
  };

  if (loading) {
    return (
      <div className="buyer-layout" style={{ justifyContent: "center", alignItems: "center" }}>
        <p style={{ color: "#64748b", fontWeight: "600" }}>Loading your VRM AI Buyer Portal…</p>
      </div>
    );
  }

  const { company, subscriptions, payments } = data;

  const getStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "ACTIVE") return <span className="buyer-badge buyer-badge-active"><CheckCircle size={12} style={{ display: "inline", marginRight: 4 }} /> Active</span>;
    if (s === "SUSPENDED" || s === "DISABLED") return <span className="buyer-badge buyer-badge-suspended"><AlertTriangle size={12} style={{ display: "inline", marginRight: 4 }} /> Suspended</span>;
    return <span className="buyer-badge buyer-badge-pending"><Clock size={12} style={{ display: "inline", marginRight: 4 }} /> {s || "Pending"}</span>;
  };

  return (
    <div className="buyer-layout">
      {/* SIDEBAR */}
      <div className="buyer-sidebar">
        <div className="buyer-sidebar-logo">
          <img src={vrmLogo} alt="VRM AI Logo" style={{ height: "48px", width: "auto", mixBlendMode: "darken" }} />
          <div>
            <div className="buyer-sidebar-logo-text">VRM AI</div>
            <div className="buyer-sidebar-logo-sub">Buyer Portal</div>
          </div>
        </div>

        <div className={`buyer-nav-item ${activeTab === "Dashboard" ? "active" : ""}`} onClick={() => setActiveTab("Dashboard")}>
          <LayoutDashboard size={18} /> Dashboard
        </div>
        <div className={`buyer-nav-item ${activeTab === "Products" ? "active" : ""}`} onClick={() => setActiveTab("Products")}>
          <Package size={18} /> My Products
        </div>
        <div className={`buyer-nav-item ${activeTab === "Subscriptions" ? "active" : ""}`} onClick={() => setActiveTab("Subscriptions")}>
          <Calendar size={18} /> Subscriptions
        </div>
        <div className={`buyer-nav-item ${activeTab === "Payments" ? "active" : ""}`} onClick={() => setActiveTab("Payments")}>
          <CreditCard size={18} /> Payments
        </div>
        <div className={`buyer-nav-item ${activeTab === "Agreements" ? "active" : ""}`} onClick={() => setActiveTab("Agreements")}>
          <FileText size={18} /> Agreements
        </div>
        <div className={`buyer-nav-item ${activeTab === "Profile" ? "active" : ""}`} onClick={() => setActiveTab("Profile")}>
          <User size={18} /> Profile
        </div>

        <div style={{ marginTop: "auto", paddingTop: "20px", borderTop: "1px solid #1e293b" }}>
          <div className="buyer-nav-item" onClick={handleLogout} style={{ color: "#ef4444" }}>
            <LogOut size={18} /> Sign Out
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="buyer-main">
        {/* HEADER */}
        <div className="buyer-header">
          <div>
            <div className="buyer-header-title">Welcome, {buyerName}</div>
            <div style={{ fontSize: "13px", color: "#64748b" }}>{companyName}</div>
          </div>
          <div className="buyer-header-user">
            <div className="buyer-avatar">{buyerName.charAt(0).toUpperCase()}</div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="buyer-content">
          {/* STATS OVERVIEW */}
          <div className="buyer-stats-grid">
            <div className="buyer-stat-card">
              <div className="buyer-stat-title">Active Products</div>
              <div className="buyer-stat-value">{data.totalActiveProducts || subscriptions.length || 1}</div>
            </div>
            <div className="buyer-stat-card">
              <div className="buyer-stat-title">Next Renewal Date</div>
              <div className="buyer-stat-value" style={{ fontSize: "18px" }}>
                {subscriptions.length > 0 && subscriptions[0].nextBillingDate
                  ? subscriptions[0].nextBillingDate
                  : "30 Oct 2026"}
              </div>
            </div>
            <div className="buyer-stat-card">
              <div className="buyer-stat-title">Account Status</div>
              <div style={{ marginTop: "4px" }}>{getStatusBadge("ACTIVE")}</div>
            </div>
          </div>

          {/* TAB: DASHBOARD or PRODUCTS */}
          {(activeTab === "Dashboard" || activeTab === "Products") && (
            <div className="buyer-card">
              <div className="buyer-card-title">
                <span>My Active Products</span>
                <ShieldCheck size={20} style={{ color: "#4f46e5" }} />
              </div>

              {subscriptions.length === 0 ? (
                <div style={{ padding: "20px", background: "#f8fafc", borderRadius: "8px", textAlign: "center", color: "#64748b" }}>
                  No subscriptions active yet. Please contact support.
                </div>
              ) : (
                subscriptions.map((sub) => (
                  <div key={sub.id} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "20px", marginBottom: "16px", background: "#fafafa" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <h3 style={{ margin: 0, color: "#0f172a", fontSize: "18px" }}>{sub.productName || "VRM AI Product"}</h3>
                      {getStatusBadge(sub.status)}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", fontSize: "14px", color: "#475569", marginBottom: "16px" }}>
                      <div><strong>Plan:</strong> {sub.planType || "Monthly"}</div>
                      <div><strong>Start Date:</strong> {sub.startDate || "30 Sep 2026"}</div>
                      <div><strong>Next Billing:</strong> {sub.nextBillingDate || "30 Oct 2026"}</div>
                    </div>

                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <a
                        href={
                          sub.productUrl && !sub.productUrl.includes("app.vrmaitechnology.com")
                            ? sub.productUrl
                            : "https://face-recognition.vrmaitechnology.com"
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="buyer-btn buyer-btn-primary"
                        style={{ textDecoration: "none" }}
                      >
                        Launch Product <ExternalLink size={16} />
                      </a>
                      <button className="buyer-btn buyer-btn-secondary" onClick={handleDownloadAgreement} disabled={downloadingAgreement}>
                        {downloadingAgreement ? "Preparing PDF…" : "View Agreement ↗"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB: SUBSCRIPTIONS */}
          {activeTab === "Subscriptions" && (
            <div className="buyer-card">
              <div className="buyer-card-title">Subscriptions Management</div>
              <table className="buyer-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Plan</th>
                    <th>Start Date</th>
                    <th>Expiry Date</th>
                    <th>Next Billing</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td><strong>{sub.productName}</strong></td>
                      <td>{sub.planType}</td>
                      <td>{sub.startDate || "-"}</td>
                      <td>{sub.expiryDate || "-"}</td>
                      <td>{sub.nextBillingDate || "-"}</td>
                      <td>{getStatusBadge(sub.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: PAYMENTS */}
          {activeTab === "Payments" && (
            <div className="buyer-card">
              <div className="buyer-card-title">Payment History</div>
              {payments.length === 0 ? (
                <p style={{ color: "#64748b", fontSize: "14px" }}>No payment history found.</p>
              ) : (
                <table className="buyer-table">
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td><strong>{p.transactionId}</strong></td>
                        <td>{p.paymentDate || "-"}</td>
                        <td>{p.paymentMode}</td>
                        <td>₹{p.amountPaid}</td>
                        <td>
                          {p.paymentVerified ? (
                            <span className="buyer-badge buyer-badge-active">Paid & Verified</span>
                          ) : (
                            <span className="buyer-badge buyer-badge-pending">Pending Verification</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TAB: AGREEMENTS */}
          {activeTab === "Agreements" && (
            <div className="buyer-card">
              <div className="buyer-card-title">Legal Agreements</div>
              <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>
                  {company?.companyName || companyName} Subscription Agreement
                </div>
                <div style={{ fontSize: "14px", color: "#475569", marginBottom: "16px" }}>
                  <div><strong>Signature:</strong> {company?.agreementSignature || agreement?.signature || "Accepted"}</div>
                  <div><strong>Category:</strong> {company?.productCategory || agreement?.productCategory || "Visionix AI"}</div>
                </div>
                <button className="buyer-btn buyer-btn-primary" onClick={handleDownloadAgreement} disabled={downloadingAgreement}>
                  {downloadingAgreement ? "Downloading PDF…" : "Download Signed Agreement (PDF)"}
                </button>
              </div>
            </div>
          )}

          {/* TAB: PROFILE */}
          {activeTab === "Profile" && (
            <div className="buyer-card">
              <div className="buyer-card-title">Company & Account Profile</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", fontSize: "14px" }}>
                <div><strong>Company Name:</strong> {company?.companyName}</div>
                <div><strong>Registered Email:</strong> {company?.companyEmail}</div>
                <div><strong>Phone Number:</strong> {company?.phone || "-"}</div>
                <div><strong>Address:</strong> {company?.address || "-"}</div>
                <div><strong>Purchaser Name:</strong> {company?.purchaserName || "-"}</div>
                <div><strong>Designation:</strong> {company?.purchaserDesignation || "-"}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BuyerDashboard;
